import { useState } from "react";
import { ChevronDown, X, Sparkles } from "lucide-react";
import { useAdsSpyStore } from "../store/adsSpyStore";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

const AI_FILTER_KEYS = [
  "asset_type",
  "visual_format",
  "messaging_angle",
  "hook_tactic",
  "offer_type",
] as const;

const AI_FILTER_LABEL_KEYS: Record<string, string> = {
  asset_type: "asset-type",
  visual_format: "visual-format",
  messaging_angle: "messaging-angle",
  hook_tactic: "hook-tactic",
  offer_type: "offer-type",
};

const SELECT_CLASS =
  "appearance-none text-[12.5px] font-medium pl-3.5 py-[7px] pr-8 rounded-xl border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-[#5050EA] focus:ring-1 focus:ring-[#5050EA]/20 transition-all duration-200 cursor-pointer";

function SelectWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {children}
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
    </div>
  );
}

export default function AdsSpyFilters() {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const getAiFilterLabel = (key: string) => t(AI_FILTER_LABEL_KEYS[key] || key, key);

  const {
    brands,
    filters,
    setFilter,
    clearAiFilters,
    aiFiltersData,
    pagination,
    loadAds,
    loadAiFilters,
  } = useAdsSpyStore();

  const [aiFiltersOpen, setAiFiltersOpen] = useState(false);

  const hasAiFilters = aiFiltersData
    ? AI_FILTER_KEYS.some(
        (k) => aiFiltersData[k] && aiFiltersData[k].length > 0
      )
    : false;

  const activeAiFilters = AI_FILTER_KEYS.filter((k) => filters[k]);

  const handleFilterChange = (
    key: keyof typeof filters,
    value: string
  ) => {
    setFilter(key, value as never);
    // status filter is client-side only, no need to reload from API
    if (key === "status") return;
    if (key === "brand_id") {
      clearAiFilters();
      loadAiFilters();
    }
    setTimeout(() => loadAds(true), 0);
  };

  const totalAds = pagination.total;

  return (
    <div className="space-y-3 mb-6">
      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Brand filter */}
        <SelectWrapper>
          <select
            value={filters.brand_id}
            onChange={(e) => handleFilterChange("brand_id", e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">{t("all-brands", "All brands ({n})").replace("{n}", String(totalAds))}</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.ad_count || 0})
              </option>
            ))}
          </select>
        </SelectWrapper>

        {/* Media type filter */}
        <SelectWrapper>
          <select
            value={filters.media_type}
            onChange={(e) => handleFilterChange("media_type", e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">{t("all-types", "All types")}</option>
            <option value="Video">Video</option>
            <option value="Image">{t("image", "Image")}</option>
          </select>
        </SelectWrapper>

        {/* Status filter */}
        <SelectWrapper>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">{t("all-statuses", "All statuses")}</option>
            <option value="active">{t("active-status", "Active")}</option>
            <option value="expired">{t("expired-status", "Expired")}</option>
          </select>
        </SelectWrapper>

        {/* Sort */}
        <SelectWrapper>
          <select
            value={filters.sort}
            onChange={(e) =>
              handleFilterChange("sort", e.target.value)
            }
            className={SELECT_CLASS}
          >
            <option value="rank">{t("best-ranked", "Best ranked")}</option>
            <option value="newest">{t("most-recent", "Most recent")}</option>
            <option value="oldest">{t("oldest", "Oldest")}</option>
          </select>
        </SelectWrapper>

        {/* AI Filters toggle */}
        {hasAiFilters && (
          <button
            onClick={() => setAiFiltersOpen(!aiFiltersOpen)}
            className="text-[12.5px] font-medium px-3.5 py-[7px] rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-[#5050EA] hover:text-[#5050EA] transition-all duration-200 flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t("ai-filters", "AI Filters")}
            <ChevronDown
              className={`w-3 h-3 transition-transform duration-200 ${aiFiltersOpen ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {/* AI Filters row */}
      {aiFiltersOpen && aiFiltersData && (
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {AI_FILTER_KEYS.map((key) => {
            const values = aiFiltersData[key] || [];
            if (values.length === 0) return null;
            return (
              <div key={key} className="relative">
                <select
                  value={filters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.value)}
                  className="appearance-none text-xs font-medium pl-3 py-1.5 pr-7 rounded-xl border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-[#5050EA] focus:ring-1 focus:ring-[#5050EA]/20 transition-all duration-200 cursor-pointer"
                >
                  <option value="">{getAiFilterLabel(key)}</option>
                  {values.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              </div>
            );
          })}
          <button
            onClick={() => {
              clearAiFilters();
              setTimeout(() => loadAds(true), 0);
            }}
            className="text-xs text-gray-400 hover:text-[#5050EA] transition-all duration-200"
          >
            {t("clear", "Clear")}
          </button>
        </div>
      )}

      {/* Active filter pills */}
      {activeAiFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeAiFilters.map((key) => (
            <button
              key={key}
              onClick={() => {
                setFilter(key, "" as never);
                setTimeout(() => loadAds(true), 0);
              }}
              className="inline-flex items-center text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#EEEEFF] text-[#5050EA] border border-[#D8D8F8] hover:bg-[#DDDDF8] transition-all duration-200"
            >
              <span className="text-[10px] text-gray-400 mr-1">
                {getAiFilterLabel(key)}:
              </span>
              {filters[key]}
              <X className="w-3 h-3 ml-1 text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
